import React from "react";
import Sidebar from "./Sidebar";
import { useDispatch, useSelector } from 'react-redux';
import Header from "./Header";
import ImpersonationBanner from "./ImpersonationBanner";
import NewIncidentModal from "@/Modal/NewIncidentModal";
import { openIncidentModal, setIncidentModalState } from "@/Redux/Action/organizationActions";
import socketService from "../services/socketService";
import IncomingCallScreen from "../components/calls/IncomingCallScreen";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiEndPoint, persistMeetModeForNextMeet } from "../services/apiUttiLities";
import axios from "axios";
import { toast } from "sonner";
import { DutyCheckJobProvider } from "@/context/DutyCheckJobContext";
import DutyCheckJobOverlay from "@/components/DutyCheckJobOverlay";
import SendDutyCheckModal from "@/page/SendDutyCheckModal";
import { isWebAdmin } from "@/auth/webAccess";
import SupportSessionBanner from "./SupportSessionBanner";
import InstitutionSecurityBanner from "./InstitutionSecurityBanner";
import { creationAccessForUser } from "../auth/incidentPermissions";
import { isPlatformSuperadmin } from "@/auth/saasSession";
import { Menu } from "lucide-react";

const SAAS_LANGUAGE_KEY = 'disasterms_saas_language';
const SAAS_LANGUAGE_EVENT = 'disasterms:saas-language-changed';

const readSaaSLanguage = () => (
  typeof window !== 'undefined' && window.localStorage.getItem(SAAS_LANGUAGE_KEY) === 'ar' ? 'ar' : 'en'
);

const institutionMobileMediaQuery = '(max-width: 1023px)';

const readInstitutionMobileViewport = () => {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function') {
    return window.matchMedia(institutionMobileMediaQuery).matches;
  }
  return window.innerWidth < 1024;
};

export default function Layout({ children }) {
  const dispatch = useDispatch();
  const { isSidebarVisible, incidentModalState } = useSelector((state) => state.organization);
  let currentUser = null;
  try {
    currentUser = JSON.parse(localStorage.getItem('user') || 'null');
  } catch (_) {
    currentUser = null;
  }
  const isAdmin = isWebAdmin(currentUser);
  const isSaaSAccount = isPlatformSuperadmin(currentUser);
  const [saasLanguage, setSaaSLanguage] = React.useState(readSaaSLanguage);
  const { canCreateIncident, canCreateDrill } = creationAccessForUser(currentUser);
  const canCreateIncidentOrDrill = canCreateIncident || canCreateDrill;

  const navigate = useNavigate();
  const location = useLocation();
  const contentAreaRef = React.useRef(null);
  const [incomingCall, setIncomingCall] = React.useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [institutionMobileViewport, setInstitutionMobileViewport] = React.useState(
    readInstitutionMobileViewport
  );
  const timeoutRef = React.useRef(null);
  const institutionSidebarDrawerRef = React.useRef(null);

  React.useEffect(() => {
    if (isSaaSAccount || typeof window === 'undefined') return undefined;

    const mediaQuery = typeof window.matchMedia === 'function'
      ? window.matchMedia(institutionMobileMediaQuery)
      : null;
    const syncViewport = (event) => {
      const matches = typeof event?.matches === 'boolean'
        ? event.matches
        : readInstitutionMobileViewport();
      setInstitutionMobileViewport(matches);
      if (!matches) setMobileSidebarOpen(false);
    };

    syncViewport(mediaQuery);
    if (mediaQuery?.addEventListener) {
      mediaQuery.addEventListener('change', syncViewport);
    } else {
      mediaQuery?.addListener?.(syncViewport);
    }

    return () => {
      if (mediaQuery?.removeEventListener) {
        mediaQuery.removeEventListener('change', syncViewport);
      } else {
        mediaQuery?.removeListener?.(syncViewport);
      }
    };
  }, [isSaaSAccount]);

  React.useEffect(() => {
    if (isSaaSAccount) return;
    setMobileSidebarOpen(false);
  }, [isSaaSAccount, location.pathname, location.search]);

  React.useEffect(() => {
    if (isSaaSAccount || !institutionMobileViewport || !mobileSidebarOpen) return undefined;

    const focusTimer = window.setTimeout(() => {
      institutionSidebarDrawerRef.current
        ?.querySelector('[data-sidebar-close]')
        ?.focus();
    }, 0);
    const handleEscape = (event) => {
      if (event.key === 'Escape') setMobileSidebarOpen(false);
    };
    window.addEventListener('keydown', handleEscape);

    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener('keydown', handleEscape);
    };
  }, [institutionMobileViewport, isSaaSAccount, mobileSidebarOpen]);

  React.useEffect(() => {
    if (!isSaaSAccount) return undefined;
    const syncLanguage = () => setSaaSLanguage(readSaaSLanguage());
    window.addEventListener(SAAS_LANGUAGE_EVENT, syncLanguage);
    window.addEventListener('storage', syncLanguage);
    return () => {
      window.removeEventListener(SAAS_LANGUAGE_EVENT, syncLanguage);
      window.removeEventListener('storage', syncLanguage);
    };
  }, [isSaaSAccount]);

  React.useEffect(() => {
    if (!isSaaSAccount) return;
    const pageScroller = contentAreaRef.current?.querySelector('main');
    if (pageScroller) pageScroller.scrollTop = 0;
  }, [isSaaSAccount, location.pathname, location.search]);

  React.useEffect(() => {
    if (isSaaSAccount) return undefined;
    console.log("🔌 Layout mounted, registering global call listeners...");
    
    // Request notification permissions
    if (typeof window !== 'undefined' && 'Notification' in window) {
      Notification.requestPermission();
    }

    const fetchCurrentCall = async () => {
      const user = JSON.parse(localStorage.getItem("user"));
      const userId = user?.id || user?._id;
      if (!userId) return;
      
      try {
        console.log("📡 Layout: Checking for existing active/ringing calls for user:", user.id);
        const res = await axios.get(`${ApiEndPoint}/api/calls/active`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        
        if (res.data?.data) {
          const call = res.data.data;
          // Only show if the user is invited or creator
          const isInvited = call.invitedUsers?.includes(String(userId)) || call.createdBy === String(userId);
          if (isInvited && call.status === 'ringing' && call.createdBy !== String(userId)) {
            console.log("☎️ Layout: Recovered ringing call state:", call);
            setIncomingCall(call);
          }
        }
      } catch (e) {
        console.warn("Layout: Failed to fetch initial call state", e);
      }
    };

    fetchCurrentCall();

    // Listen for new calls
    const unsubscribeNewCall = socketService.on('new_call', (data) => {
      console.log("☎️ GLOBAL: Received new_call event:", data);
      
      const user = JSON.parse(localStorage.getItem("user"));
      const userId = user?.id || user?._id;
      if (data.createdBy && userId && String(data.createdBy) === String(userId)) {
        console.log("☎️ GLOBAL: Ignoring new_call because I am the caller");
        return;
      }

      console.log("🔌 Socket status:", socketService.isConnected() ? "CONNECTED" : "DISCONNECTED");
      
      // Show Browser Notification
      if (Notification.permission === "granted") {
        const title = `Incoming ${data.callType === 'video' ? 'Video' : 'Voice'} Call`;
        const bodyText = `${data.callerName || 'Teammate'} is calling you${data.incidentTitle ? ` about ${data.incidentTitle}` : data.incidentId ? ` about Incident #${data.incidentId}` : ''}`;
        new Notification(title, {
          body: bodyText,
          icon: '/favicon.ico',
          tag: 'incoming-call',
          renotify: true
        });
      }

      setIncomingCall(data);

      // Set timeout for 30 seconds
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        console.log("⏰ GLOBAL: Call timed out locally");
        setIncomingCall(null);
      }, 50000);
    });

    // Listen for status updates
    const unsubscribeStatus = socketService.on('call_status_update', (data) => {
      console.log("📡 GLOBAL: Received call_status_update event:", data);
      setIncomingCall((prev) => {
        if (prev && String(prev.id) === String(data.id)) {
          if (data.status !== 'ringing') {
            console.log(`🔇 GLOBAL: Call status changed to ${data.status}, clearing overlay`);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            return null;
          }
        }
        return prev;
      });
    });

    const unsubscribeNotification = socketService.on('new_incident_notification', (data) => {
      console.log("🔔 GLOBAL: Received new_incident_notification:", data);
      
      // Check if we are already on the chat page for this incident
      const currentPath = window.location.pathname;
      const chatPath = `/incident-details/${data.incidentId}/chat`;
      
      if (currentPath === chatPath) {
        console.log("🔔 GLOBAL: User is already on the chat page, skipping toast");
        return;
      }

      const msg = data.message;
      const title = data.incidentTitle || "New Message";
      
      toast.info(title, {
        description: `${msg.username}: ${msg.text || (msg.type === 'IMAGE' ? 'Sent an image' : 'Sent an attachment')}`,
        action: {
          label: "View Chat",
          onClick: () => navigate(chatPath)
        },
        duration: 5000
      });

      // Also trigger a browser notification if permitted
      if (Notification.permission === "granted") {
        new Notification(`New Message in ${title}`, {
          body: `${msg.username}: ${msg.text || 'New attachment'}`,
          icon: '/favicon.ico',
          tag: `chat-${data.incidentId}`
        });
      }
    });

    const unsubscribeIncidentAccessRemoved = socketService.on('incident_access_removed', (data) => {
      const incidentId = String(data?.incident_id || '').trim();
      if (!incidentId) return;
      window.dispatchEvent(new CustomEvent('incidentAccessRemoved', { detail: { incident_id: incidentId } }));
      const currentPath = window.location.pathname || '';
      const escapedIncidentId = incidentId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      if (new RegExp(`^/incident-details/${escapedIncidentId}(?:/chat)?/?$`).test(currentPath)) {
        toast.error('Your access to this incident has been removed.');
        navigate('/dashboard', { replace: true });
      }
    });

    return () => {
      console.log("🔌 Layout unmounting, cleaning up call listeners...");
      unsubscribeNewCall();
      unsubscribeStatus();
      unsubscribeNotification();
      unsubscribeIncidentAccessRemoved();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isSaaSAccount, navigate]);

  React.useEffect(() => {
    if (isSaaSAccount) return;
    if (typeof window === 'undefined') return;
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('report_incident') !== 'true') return;
    if (canCreateIncidentOrDrill) dispatch(openIncidentModal());
    const newUrl = window.location.pathname + window.location.search.replace(/[?&]report_incident=true/, '');
    window.history.replaceState({}, document.title, newUrl);
  }, [canCreateIncidentOrDrill, dispatch, isSaaSAccount]);

  const handleAnswer = async () => {
    if (!incomingCall) return;
    
    try {
      const token = localStorage.getItem('access_token');
      // Update status to active
      await axios.patch(`${ApiEndPoint}/api/calls/${incomingCall.id}/status?status=active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const roomName = incomingCall.roomName;
      const callType = incomingCall.callType;
      const incidentId = incomingCall.incidentId;
      
      setIncomingCall(null);
      persistMeetModeForNextMeet(callType);
      let url = `/meets?meeting_id=${encodeURIComponent(roomName || "")}&meetMode=${callType}&callId=${incomingCall.id}`;
      if (incidentId) {
        url += `&incidentId=${encodeURIComponent(incidentId)}`;
      }
      navigate(url);
    } catch (error) {
      console.error("Failed to answer call:", error);
    }
  };

  const handleDecline = async (status = 'declined') => {
    if (!incomingCall) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(`${ApiEndPoint}/api/calls/${incomingCall.id}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIncomingCall(null);
    } catch (error) {
      console.error(`Failed to ${status} call:`, error);
    }
  };

  const handleIncidentCreated = (newIncident) => {
    // Dispatch a global event so the Dashboard (if mounted) can refresh
    const event = new CustomEvent('dashboardRefresh', { detail: newIncident });
    window.dispatchEvent(event);
  };

  const RuntimeProvider = isSaaSAccount ? React.Fragment : DutyCheckJobProvider;

  return (
    <RuntimeProvider>
    <div
      className={`h-screen flex overflow-hidden ${isSaaSAccount ? 'dms-saas-shell saas-shell-root bg-[#070a1c]' : 'bg-gray-50'}`}
      dir={isSaaSAccount ? (saasLanguage === 'ar' ? 'rtl' : 'ltr') : undefined}
      lang={isSaaSAccount ? saasLanguage : undefined}
    >
      {/* Sidebar */}
      {isSidebarVisible && (
        isSaaSAccount ? (
          <Sidebar />
        ) : (
          <>
            <button
              type="button"
              onClick={() => setMobileSidebarOpen(false)}
              aria-label="Close navigation menu"
              aria-hidden={!mobileSidebarOpen}
              tabIndex={mobileSidebarOpen ? 0 : -1}
              data-testid="institution-sidebar-backdrop"
              data-state={mobileSidebarOpen ? 'open' : 'closed'}
              className={`fixed inset-0 z-[60] bg-slate-950/40 backdrop-blur-[1px] transition-opacity duration-300 lg:hidden ${
                mobileSidebarOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
              }`}
            />
            <div
              ref={institutionSidebarDrawerRef}
              data-testid="institution-sidebar-drawer"
              data-state={mobileSidebarOpen ? 'open' : 'closed'}
              aria-hidden={institutionMobileViewport && !mobileSidebarOpen}
              inert={institutionMobileViewport && !mobileSidebarOpen ? true : undefined}
              className={`fixed inset-y-0 left-0 z-[70] h-full shrink-0 transform-gpu transition-transform duration-300 ease-out lg:relative lg:inset-auto lg:z-auto lg:translate-x-0 lg:pointer-events-auto ${
                mobileSidebarOpen
                  ? 'translate-x-0 pointer-events-auto'
                  : '-translate-x-full pointer-events-none'
              }`}
            >
              <Sidebar
                mobile={institutionMobileViewport}
                onRequestClose={() => setMobileSidebarOpen(false)}
              />
            </div>
          </>
        )
      )}

      {/* Main column */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {/* Impersonation banner (only visible during an impersonation session) */}
        <ImpersonationBanner />
        <SupportSessionBanner />
        <InstitutionSecurityBanner enabled={!isSaaSAccount && isAdmin} />
        {/* Responsive Header */}
        <Header
          onOpenSidebar={
            !isSaaSAccount && isSidebarVisible
              ? () => setMobileSidebarOpen(true)
              : undefined
          }
        />

        {/* Content area */}
        <div ref={contentAreaRef} className={`flex-1 min-h-0 flex flex-col overflow-hidden ${isSaaSAccount ? 'bg-[#070a1c]' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
          {children}
        </div>
      </div>

      {!isSaaSAccount
        && isSidebarVisible
        && location.pathname.includes('incident-details')
        && (
          <button
            type="button"
            onClick={() => setMobileSidebarOpen(true)}
            className="fixed left-3 top-3 z-[55] flex h-11 w-11 items-center justify-center rounded-[10px] border border-slate-200 bg-white/95 text-slate-700 shadow-lg backdrop-blur transition-colors hover:bg-white hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 lg:hidden"
            aria-label="Open navigation menu"
            title="Open navigation menu"
            data-testid="incident-mobile-sidebar-trigger"
          >
            <Menu className="h-5 w-5" strokeWidth={1.8} />
          </button>
        )}

      {/* Global New Incident Modal */}
      {!isSaaSAccount && canCreateIncidentOrDrill && incidentModalState !== 'closed' && (
        <NewIncidentModal
          isOpen={incidentModalState === 'open'}
          isMinimized={incidentModalState === 'minimized'}
          onMinimize={() => dispatch(setIncidentModalState('minimized'))}
          onRestore={() => dispatch(setIncidentModalState('open'))}
          onClose={() => dispatch(setIncidentModalState('closed'))}
          onIncidentCreated={handleIncidentCreated}
        />
      )}
      {/* Incoming Call Screen Overlay */}
      {!isSaaSAccount && incomingCall && (isAdmin || incomingCall.incidentId) && (
        <IncomingCallScreen 
          callData={{
            callerName: incomingCall.callerName || "Teammate",
            callType: incomingCall.callType,
            incidentTitle: incomingCall.incidentTitle || (incomingCall.incidentId ? `Incident #${incomingCall.incidentId}` : "Direct Call"),
            incidentId: incomingCall.incidentId
          }} 
          onAnswer={handleAnswer} 
          onDecline={handleDecline} 
        />
      )}
      {!isSaaSAccount && isAdmin && <SendDutyCheckModal />}
      {!isSaaSAccount && isAdmin && <DutyCheckJobOverlay />}
    </div>
    </RuntimeProvider>
  );
}
